import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Calendar,
  Filter,
  ArrowLeft,
  Building,
  Layers,
  Cpu,
  Wrench,
  Database,
  TrendingUp,
  Download,
  RefreshCw
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Dropdown from '../../components/ui/Dropdown';
import { adminApi } from '../../services/api';
import { AdminSubscriptionRecord } from '../../types';

interface MonthlyInsight {
  month: string;
  year: number;
  monthKey: string;
  customSolution: number;
  baseCost: number;
  llmUsage: number;
  storage: number;
  total: number;
}

interface TenantOption {
  value: string;
  label: string;
}

const SUBSCRIPTION_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'e-commerce', label: 'E-Commerce' },
  { value: 'internal', label: 'Internal' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'tech', label: 'Tech' },
];

const SubscriptionInsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<AdminSubscriptionRecord[]>([]);
  
  // Filters
  const [selectedType, setSelectedType] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllSubscriptions({ limit: 1000 });
      setSubscriptions(response.subscriptions || []);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate tenant options from subscriptions
  const tenantOptions: TenantOption[] = useMemo(() => {
    const options: TenantOption[] = [{ value: '', label: 'All Tenants' }];
    const seen = new Set<string>();
    
    subscriptions.forEach(sub => {
      if (sub.tenantId && !seen.has(sub.tenantId)) {
        seen.add(sub.tenantId);
        options.push({
          value: sub.tenantId,
          label: sub.tenantName || sub.tenantId
        });
      }
    });
    
    return options;
  }, [subscriptions]);

  // Filter subscriptions based on selected filters
  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter(sub => {
      if (selectedType && (!sub.types || !sub.types.includes(selectedType))) {
        return false;
      }
      if (selectedTenant && sub.tenantId !== selectedTenant) {
        return false;
      }
      return true;
    });
  }, [subscriptions, selectedType, selectedTenant]);

  // Generate insights data based on filters
  const insightsData: MonthlyInsight[] = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const months: MonthlyInsight[] = [];
    
    const currentDate = new Date(start);
    currentDate.setDate(1);
    
    while (currentDate <= end) {
      const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const monthName = currentDate.toLocaleString('default', { month: 'short' });
      const year = currentDate.getFullYear();
      
      // Calculate totals for this month based on filtered subscriptions
      let baseCost = 0;
      let llmUsage = 0;
      let customSolution = 0;
      let storage = 0;
      
      filteredSubscriptions.forEach(sub => {
        const subCreated = new Date(sub.createdAt);
        if (subCreated <= currentDate) {
          // Base cost from subscription amount
          baseCost += sub.billing?.amount || 0;
          // Simulated values for demo (in real app these would come from API)
          llmUsage += Math.floor(Math.random() * 80) + 20;
          customSolution += Math.random() > 0.7 ? Math.floor(Math.random() * 200) + 50 : 0;
          storage += Math.floor(Math.random() * 30) + 5;
        }
      });
      
      months.push({
        month: monthName,
        year,
        monthKey,
        customSolution: Math.round(customSolution * 100) / 100,
        baseCost: Math.round(baseCost * 100) / 100,
        llmUsage: Math.round(llmUsage * 100) / 100,
        storage: Math.round(storage * 100) / 100,
        total: Math.round((customSolution + baseCost + llmUsage + storage) * 100) / 100,
      });
      
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return months;
  }, [filteredSubscriptions, startDate, endDate]);

  // Calculate totals
  const totals = useMemo(() => {
    return insightsData.reduce(
      (acc, item) => ({
        customSolution: acc.customSolution + item.customSolution,
        baseCost: acc.baseCost + item.baseCost,
        llmUsage: acc.llmUsage + item.llmUsage,
        storage: acc.storage + item.storage,
        total: acc.total + item.total,
      }),
      { customSolution: 0, baseCost: 0, llmUsage: 0, storage: 0, total: 0 }
    );
  }, [insightsData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const handleReset = () => {
    setSelectedType('');
    setSelectedTenant('');
    const currentYear = new Date().getFullYear();
    setStartDate(`${currentYear}-01-01`);
    setEndDate(new Date().toISOString().split('T')[0]);
  };

  const chartData = insightsData.map(item => ({
    name: `${item.month} ${item.year}`,
    'Custom Solution': item.customSolution,
    'Base Cost': item.baseCost,
    'LLM Usage': item.llmUsage,
    'Storage': item.storage,
    'Total': item.total,
  }));

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate('/admin/subscriptions')}
          className="!p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold dark:text-white text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-violet-500" />
            Subscription Insights
          </h1>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">
            Analyze subscription revenue and usage analytics
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h3 className="font-semibold dark:text-white text-gray-900">Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Subscription Type */}
          <div>
            <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
              Subscription Type
            </label>
            <Dropdown
              options={SUBSCRIPTION_TYPES}
              value={selectedType}
              onChange={setSelectedType}
              placeholder="All Types"
            />
          </div>

          {/* Tenant */}
          <div>
            <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
              Tenant
            </label>
            <Dropdown
              options={tenantOptions}
              value={selectedTenant}
              onChange={setSelectedTenant}
              placeholder="All Tenants"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-gray-700 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-gray-700 border-gray-300 dark:bg-gray-800 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-end gap-2">
            <Button
              variant="secondary"
              onClick={handleReset}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Wrench className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-xl font-bold dark:text-white text-gray-900">
                {formatCurrency(totals.customSolution)}
              </p>
              <p className="text-sm dark:text-gray-400 text-gray-600">Custom Solution</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Layers className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xl font-bold dark:text-white text-gray-900">
                {formatCurrency(totals.baseCost)}
              </p>
              <p className="text-sm dark:text-gray-400 text-gray-600">Base Cost</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Cpu className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-xl font-bold dark:text-white text-gray-900">
                {formatCurrency(totals.llmUsage)}
              </p>
              <p className="text-sm dark:text-gray-400 text-gray-600">LLM Usage</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-500/10">
              <Database className="w-5 h-5 text-teal-500" />
            </div>
            <div>
              <p className="text-xl font-bold dark:text-white text-gray-900">
                {formatCurrency(totals.storage)}
              </p>
              <p className="text-sm dark:text-gray-400 text-gray-600">Storage</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4 bg-primary/5 dark:bg-primary/10 border-primary/20">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xl font-bold text-primary">
                {formatCurrency(totals.total)}
              </p>
              <p className="text-sm text-primary/70">Total Revenue</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Total Revenue Over Time */}
        <Card>
          <h3 className="font-semibold dark:text-white text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Total Revenue Over Time
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: number) => [formatCurrency(value), '']}
                />
                <Area
                  type="monotone"
                  dataKey="Total"
                  stroke="#6366f1"
                  fill="url(#totalGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Revenue Breakdown */}
        <Card>
          <h3 className="font-semibold dark:text-white text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-violet-500" />
            Revenue Breakdown by Category
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: number) => [formatCurrency(value), '']}
                />
                <Legend />
                <Bar dataKey="Custom Solution" fill="#f97316" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Base Cost" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="LLM Usage" fill="#a855f7" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Storage" fill="#14b8a6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Individual Category Trends */}
        <Card className="lg:col-span-2">
          <h3 className="font-semibold dark:text-white text-gray-900 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-500" />
            Category Trends Over Time
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: number) => [formatCurrency(value), '']}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="Custom Solution"
                  stroke="#f97316"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="Base Cost"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="LLM Usage"
                  stroke="#a855f7"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="Storage"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold dark:text-white text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            Monthly Breakdown
          </h3>
          <Button variant="secondary" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b dark:border-gray-700 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Month
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Wrench className="w-3 h-3" />
                    Custom Solution
                  </div>
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Layers className="w-3 h-3" />
                    Base Cost
                  </div>
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Cpu className="w-3 h-3" />
                    LLM Usage
                  </div>
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Database className="w-3 h-3" />
                    Storage
                  </div>
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-primary uppercase tracking-wider text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-800 divide-gray-100">
              {insightsData.map((item) => (
                <tr
                  key={item.monthKey}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
                >
                  <td className="px-6 py-4 dark:text-gray-300 text-gray-700">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">
                        {item.month} {item.year}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                    {item.customSolution > 0 ? (
                      <span className="text-orange-500 font-medium">
                        {formatCurrency(item.customSolution)}
                      </span>
                    ) : (
                      <span className="opacity-30">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                    {formatCurrency(item.baseCost)}
                  </td>
                  <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                    {formatCurrency(item.llmUsage)}
                  </td>
                  <td className="px-6 py-4 text-right dark:text-gray-400 text-gray-600">
                    {formatCurrency(item.storage)}
                  </td>
                  <td className="px-6 py-4 text-right font-bold dark:text-white text-gray-900">
                    {formatCurrency(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-primary/5 dark:bg-primary/10 font-bold border-t border-primary/20">
              <tr>
                <td className="px-6 py-4 text-primary">All Time Totals</td>
                <td className="px-6 py-4 text-right text-primary">
                  {formatCurrency(totals.customSolution)}
                </td>
                <td className="px-6 py-4 text-right text-primary">
                  {formatCurrency(totals.baseCost)}
                </td>
                <td className="px-6 py-4 text-right text-primary">
                  {formatCurrency(totals.llmUsage)}
                </td>
                <td className="px-6 py-4 text-right text-primary">
                  {formatCurrency(totals.storage)}
                </td>
                <td className="px-6 py-4 text-right text-primary text-lg">
                  {formatCurrency(totals.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionInsightsPage;
